import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

/**
 * SecurityBondingLayout — Layout fullscreen escuro para o fluxo mobile de autenticação forte.
 * Sem header, sidebar ou footer. Detecta User-Agent e exibe mensagem se acessado no Desktop.
 *
 * Doc: docs_autentica_seguranca_hard/01-fluxo-mobile-biometria.md
 * Rota: /security/bonding (pública)
 */
export function SecurityBondingLayout({ children }: Props) {
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const ua = navigator.userAgent.toLowerCase();
    const mobile = /android|iphone|ipad|ipod|mobile|tablet/.test(ua);
    setIsDesktop(!mobile);
  }, []);

  if (isDesktop) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        background: '#0a0a0f', color: '#f8fafc', fontFamily: 'system-ui, sans-serif',
        padding: '2rem', textAlign: 'center',
      }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📱</div>
        <h1 style={{ fontSize: '1.4rem', fontWeight: 700, marginBottom: '0.75rem' }}>
          Esta página deve ser acessada pelo seu celular
        </h1>
        <p style={{ fontSize: '0.95rem', color: '#94a3b8', maxWidth: 380 }}>
          Escaneie o QR Code exibido no computador com a câmera do seu celular para continuar a autenticação.
        </p>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100dvh',
      background: 'radial-gradient(circle at 50% 0%, #1e293b, #020617)',
      color: '#f8fafc',
      fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
      display: 'flex',
      flexDirection: 'column',
      overflowX: 'hidden',
      position: 'relative',
    }}>
      {/* Premium Ambient Glow */}
      <div style={{
        position: 'absolute', top: '-10%', left: '50%', transform: 'translateX(-50%)',
        width: '120vw', height: '40vh',
        background: 'radial-gradient(ellipse at center, rgba(168,85,247,0.15) 0%, rgba(0,0,0,0) 70%)',
        pointerEvents: 'none', zIndex: 0,
      }} />

      {/* Global animations */}
      <style>{`
        @keyframes fade-in-up {
          from { opacity: 0; transform: translateY(15px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes gentle-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.8; }
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        .glass-card {
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.08);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
        }
        .premium-btn {
          background: linear-gradient(135deg, #a855f7 0%, #7c3aed 100%);
          color: #ffffff;
          transition: all 0.3s ease;
          box-shadow: 0 4px 14px rgba(168, 85, 247, 0.35);
        }
        .premium-btn:active {
          transform: scale(0.97);
          box-shadow: 0 2px 8px rgba(168, 85, 247, 0.2);
        }
      `}</style>

      {/* Header com logo e ícone */}
      <header style={{
        padding: '1.25rem 1.5rem 0.75rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        borderBottom: '1px solid rgba(255,255,255,0.05)',
        zIndex: 2,
        position: 'relative',
        background: 'rgba(2, 6, 23, 0.4)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)'
      }}>
        {/* Ícone do app */}
        <img
          src="/icons/logo-shield.svg"
          alt="Aluno Conectado"
          style={{
            width: 38,
            height: 38,
            objectFit: 'contain'
          }}
        />
        <span style={{ fontSize: '1.35rem', fontWeight: 800, letterSpacing: '-0.01em', fontFamily: 'system-ui, sans-serif' }}>
          <span style={{ color: '#ffffff' }}>Aluno </span>
          <span style={{ color: '#a855f7' }}>Conectado</span>
        </span>
      </header>

      {/* Conteúdo das Fases */}
      <div className="animate-fade-in-up" style={{ 
        flex: 1, display: 'flex', flexDirection: 'column', 
        padding: '1.5rem 1.25rem', position: 'relative', zIndex: 1 
      }}>
        {children}
      </div>
    </div>
  );
}
